import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import DashboardNavbar from "../../components/DashboardNavbar";
import Sidebar from "../../components/Sidebar";
import Content from "../../components/Content";
import SessionDashboard from "./SessionDashboard";
import { Form, Grid, Dropdown } from "semantic-ui-react";
import { getGroupsData, getGroupUsersData } from "../../apis/index";
import { getAllChatHistoryCached } from "../../actions/dataConsoleAction";
import "../../styles/DashboardNavbar.css";
import "../../styles/Dashboard.css";
import "../../styles/EngagementDashboard.css";
import 'semantic-ui-css/semantic.min.css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DropLoader from '../../components/DropLoader';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';

const Index = ({ pageID, title }) => {
  const dispatch = useDispatch();
  const [toggleBtn, setToggleBtn] = useState(false);
  const [navTitle, setnavTitle] = useState("Admin Console");
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [loading, setLoading] = useState(true);

  // Global group and user state
  const [globalGroups, setGlobalGroups] = useState([]);
  const [globalSelectedGroup, setGlobalSelectedGroup] = useState("");
  const [globalUsers, setGlobalUsers] = useState([]);
  const [globalSelectedUsers, setGlobalSelectedUsers] = useState([]);
  const [globalGroupLoading, setGlobalGroupLoading] = useState(false);
  const [globalUserLoading, setGlobalUserLoading] = useState(false);
  const [globalAllUsers, setGlobalAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const initialLoadComplete = useRef(false);

  // Global date state - Initialize with last 30 days
  const [globalStartDate, setGlobalStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [globalEndDate, setGlobalEndDate] = useState(new Date());
  const [isGlobalCalendarOpen, setIsGlobalCalendarOpen] = useState(false);

  // Fetch datasets and users from chat history
  useEffect(() => {
    const fetchChatHistoryData = async () => {
      try {
        setLoading(true);
        setGlobalUserLoading(true);

        const chatHistoryData = await dispatch(getAllChatHistoryCached());

        // Extract unique datasets
        const datasetMap = new Map();
        chatHistoryData.forEach((item) => {
          if (!datasetMap.has(item.datasetId)) {
            datasetMap.set(item.datasetId, item.datasetName);
          }
        });

        const datasetsList = Array.from(datasetMap, ([datasetId, datasetName]) => ({
          datasetId,
          datasetName,
        }));
        setDatasets(datasetsList);

        // Extract unique users
        const usersList = [];
        chatHistoryData.forEach((record) => {
          if (record.createdUTC) {
            if (!usersList.some((user) => user.userId === record.userId)) {
              usersList.push({
                userId: record.userId,
                username: record.createdBy.username,
              });
            }
          }
        });

        setGlobalAllUsers(usersList);
        setGlobalUsers(usersList);

        initialLoadComplete.current = true;
        setLoading(false);
        setGlobalUserLoading(false);
      } catch (error) {
        console.error('Error fetching chat history data:', error);
        setLoading(false);
        setGlobalUserLoading(false);
      }
    };

    fetchChatHistoryData();
  }, []);

  useEffect(() => {
    setnavTitle(title);
  }, [title]);

  // Fetch groups on component mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setGlobalGroupLoading(true);
        const response = await getGroupsData();
        if (response?.data?.success && response.data.data) {
          setGlobalGroups(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setGlobalGroupLoading(false);
      }
    };

    fetchGroups();
  }, []);

  // When group changes, fetch users for that group
  useEffect(() => {
    // Skip on initial mount before data is loaded
    if (!initialLoadComplete.current) {
      return;
    }

    if (!globalSelectedGroup) {
      // Reset to all users when no group selected
      setGlobalUsers(globalAllUsers);
      setGlobalSelectedUsers([]);
      return;
    }

    const fetchGroupUsers = async () => {
      try {
        setGlobalUserLoading(true);
        const response = await getGroupUsersData(globalSelectedGroup);
        
        if (response?.data?.data) {
          const groupUsers = response.data.data.map(member => ({
            userId: member.userId,
            username: member.userDisplayName
          }));
          setGlobalUsers(groupUsers);
          // Select all users in the group by default
          setGlobalSelectedUsers(groupUsers.map(user => user.userId));
          console.log("✅ Group users set successfully:", groupUsers.length, "users");
        } else {
          console.log("⚠️ No users data found in response");
        }
      } catch (error) {
        console.error("❌ Error fetching group users:", error);
        setGlobalUsers([]);
        setGlobalSelectedUsers([]);
      } finally {
        setGlobalUserLoading(false);
      }
    };

    fetchGroupUsers();
  }, [globalSelectedGroup, globalAllUsers]);

  const toggle = () => setToggleBtn((val) => !val);

  const handleCloseButtonClick = () => {
    setToggleBtn(false);
  };

  const handleDatasetChange = (e, { value }) => {
    setSelectedDataset(value);
    const dataset = datasets.find((d) => d.datasetName === value);
    setSelectedDatasetId(dataset ? dataset.datasetId : "");
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Global Date Range Filter component
  const GlobalDateRangeFilter = () => {
    const handleDateChange = (dates) => {
      const [start, end] = dates;
      setGlobalStartDate(start);
      setGlobalEndDate(end);

      // Close calendar when both dates are selected
      if (start && end) {
        setIsGlobalCalendarOpen(false);
      }
    };

    return (
      <div className="global-date-filter" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div className="date-picker-containers">
          <div className="calendar-icon" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <FontAwesomeIcon
              icon={faCalendar}
              onClick={() => setIsGlobalCalendarOpen((prev) => !prev)}
              style={{ marginRight: '0px' }}
            />
            <DatePicker
              selected={globalStartDate}
              onChange={handleDateChange}
              startDate={globalStartDate}
              endDate={globalEndDate}
              selectsRange
              dateFormat="yyyy/MM/dd"
              className="date-picker"
              popperPlacement="bottom-end"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                  boundariesElement: 'viewport',
                },
              }}
              popperClassName="custom-datepicker-popper"
              customInput={<input style={{ display: 'none' }} />}
              open={isGlobalCalendarOpen}
              onClickOutside={() => setIsGlobalCalendarOpen(false)}
              onCalendarClose={() => setIsGlobalCalendarOpen(false)}
              isClearable={false}
              placeholderText="Select date range"
            />
          </div>
          <span>{formatDate(globalStartDate)}</span>
          <span className="date-separator" style={{ margin: '0 4px' }}>-</span>
          <span>{globalEndDate ? formatDate(globalEndDate) : 'Select end date'}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="top-wrapper">
      <Sidebar
        toggleBtn={toggleBtn}
        onCloseButtonClick={handleCloseButtonClick}
        activeMenu="Usage"
      />
      <DashboardNavbar setToggle={toggle} navTitle={navTitle} pageID={pageID} />
      <Content toggleBtn={toggleBtn}>
        <Grid stackable>
          <Grid.Row className="engagement-controls-row" style={{ position: 'relative', paddingBottom: '20px', paddingTop: '15px', paddingLeft: '20px', paddingRight: '20px' }}>
            <div className="controls-wrapper" style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              flexWrap: 'wrap',
              gap: '15px',
              justifyContent: 'space-between'
            }}>
              {/* Dataset dropdown - Left side */}
              <div className="dataset-control" style={{ minWidth: '11em' }}>
                <Form.Select
                  className="dataset-select"
                  value={selectedDataset}
                  onChange={handleDatasetChange}
                  options={[
                    { key: "all", text: "All Datasets", value: "" },
                    ...datasets.map((dataset) => ({
                      key: dataset.datasetId,
                      text: dataset.datasetName,
                      value: dataset.datasetName,
                    }))
                  ]}
                  placeholder="Select Dataset"
                  search
                  selectOnBlur={false}
                  style={{ borderRadius: "6px", minWidth: "11em", height: "40px" }}
                  icon={
                    loading ? (
                      <DropLoader className="custom-dropdown-icon" style={{ fontSize: "x-small", width: "40px", height: "40px" }} />
                    ) : (
                      <KeyboardArrowDownIcon className="custom-dropdown-icon" style={{ fontSize: "large" }} />
                    )
                  }
                />
              </div>

              {/* Spacer to push right controls to the end */}
              <div className="spacer-control" style={{ flex: '1 1 auto', minWidth: '20px' }}></div>

              {/* Right controls wrapper */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginRight: '40px' }}>
                {/* Group dropdown */}
                <div className="group-control" style={{ minWidth: '11em' }}>
                  <Form.Select
                    placeholder="Select Group"
                    value={globalSelectedGroup}
                    onChange={(e, { value }) => {
                      setGlobalSelectedGroup(value);
                    }}
                    options={[
                      { key: "all", text: "All Users", value: "" },
                      ...globalGroups.map((group) => ({
                        key: group.id,
                        text: group.name,
                        value: group.id,
                      }))
                    ]}
                    search
                    style={{
                      borderRadius: "6px",
                      minWidth: "11em",
                      height: "40px",
                    }}
                    icon={
                      globalGroupLoading ? (
                        <DropLoader
                          className="custom-dropdown-icon"
                          style={{
                            fontSize: "x-small",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          className="custom-dropdown-icon"
                          style={{ fontSize: "large" }}
                        />
                      )
                    }
                  />
                </div>

                {/* User dropdown - Multi-select with checkboxes */}
                <div className="user-control" style={{ minWidth: '11em' }}>
                  <Dropdown
                    placeholder={
                      globalSelectedUsers.length === 0
                        ? "Select User"
                        : globalSelectedUsers.length === globalUsers.length
                        ? "All Users"
                        : `${globalSelectedUsers.length} user${globalSelectedUsers.length > 1 ? 's' : ''} selected`
                    }
                    fluid
                    selection
                    search
                    scrolling
                    disabled={globalUsers.length === 0}
                    searchQuery={userSearchQuery}
                    onSearchChange={(e, { searchQuery }) => setUserSearchQuery(searchQuery)}
                    icon={
                      globalUserLoading ? (
                        <DropLoader
                          className="custom-dropdown-icon"
                          style={{
                            fontSize: "x-small",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          className="custom-dropdown-icon"
                          style={{ fontSize: "large" }}
                        />
                      )
                    }
                    style={{
                      borderRadius: "6px",
                      minWidth: "11em",
                      height: "40px",
                    }}
                  >
                    <Dropdown.Menu>
                      {/* Show "All Users" option only if search is empty OR "all users" matches the search */}
                      {(!userSearchQuery || "all users".includes(userSearchQuery.toLowerCase())) && (
                        <>
                          <Dropdown.Item
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalSelectedUsers.length === globalUsers.length) {
                                setGlobalSelectedUsers([]);
                              } else {
                                setGlobalSelectedUsers(globalUsers.map(user => user.userId));
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                              <span>{globalSelectedGroup ? "All Users in Group" : "All Users"}</span>
                              <input
                                type="checkbox"
                                checked={globalSelectedUsers.length === globalUsers.length}
                                onChange={() => {
                                  if (globalSelectedUsers.length === globalUsers.length) {
                                    setGlobalSelectedUsers([]);
                                  } else {
                                    setGlobalSelectedUsers(globalUsers.map(user => user.userId));
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      )}
                      {/* Filter users based on search query */}
                      {globalUsers
                        .filter((user) =>
                          !userSearchQuery ||
                          user.username.toLowerCase().includes(userSearchQuery.toLowerCase())
                        )
                        .map((user) => (
                          <Dropdown.Item
                            key={user.userId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalSelectedUsers.includes(user.userId)) {
                                setGlobalSelectedUsers(globalSelectedUsers.filter(id => id !== user.userId));
                              } else {
                                setGlobalSelectedUsers([...globalSelectedUsers, user.userId]);
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                              <span>{user.username}</span>
                              <input
                                type="checkbox"
                                checked={globalSelectedUsers.includes(user.userId)}
                                onChange={() => {
                                  if (globalSelectedUsers.includes(user.userId)) {
                                    setGlobalSelectedUsers(globalSelectedUsers.filter(id => id !== user.userId));
                                  } else {
                                    setGlobalSelectedUsers([...globalSelectedUsers, user.userId]);
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Global Date Range Filter */}
                <div className="calendar-control" style={{ minWidth: 'fit-content' }}>
                  <GlobalDateRangeFilter />
                </div>
              </div>
            </div>
          </Grid.Row>
        </Grid>

        <SessionDashboard
          selectedDatasetId={selectedDatasetId}
          globalStartDate={globalStartDate}
          globalEndDate={globalEndDate}
          globalSelectedUsers={globalSelectedUsers}
        />
      </Content>
    </div>
  );
};

export default Index;
